/**
 * Imperative façade filled by `startLabRuntime`. React chrome calls these
 * instead of binding DOM ids. Null until the runtime has started.
 */
import type { DrumKit, EngineTelemetry, ExternalMidiPatch, FxNode, Instrument, InstrumentSlot, SampleAssetMeta, SamplerPreset, Sf2Patch, SlotMix, SynthPreset, WavetablePreset } from "../../../src/lib/audio";
import type { LabDemoId } from "./lib/demos";
import type { LabPersistPartial } from "./lib/labStorage";
import type { MaterialMode } from "./lib/material";
import type { PresetCategoryId } from "./lib/presetBank";
import type { WtPresetCategoryId } from "./lib/wavetableBank";
import type { SamplerPresetCategoryId } from "./lib/samplerBank";
import type { PatternLoopRange } from "./lib/patternLoop";
import type { PlayScope } from "./store/transportStore";

export type LabActions = {
  enableAudio: () => Promise<void>;
  /** Latest track + master RMS/peak levels from the engine (zeros until audio is on). */
  getTelemetry: () => EngineTelemetry;
  play: () => void;
  stop: () => void;
  toggleFollowPlayhead: () => void;
  toggleArmMode: () => void;
  toggleLoop: () => void;
  setBpm: (bpm: number) => void;
  setGrid: (grid: number) => void;
  setPatternLength: (steps: number) => void;
  toggleInstrument: () => void;
  togglePatterns: () => void;
  toggleSamples: () => void;
  toggleFx: () => void;
  selectPattern: (patternId: string, options?: { focus?: boolean }) => void;
  /** Loop brace — contiguous arrange pattern range (indices into clip.patterns). */
  setPatternLoop: (range: PatternLoopRange) => void;
  getPatternLoop: () => PatternLoopRange;
  createPattern: () => void;
  renamePattern: (patternId: string, name: string) => void;
  deletePattern: (patternId: string) => void;
  duplicatePattern: (patternId: string) => void;
  reorderPatterns: (fromIndex: number, toIndex: number) => void;
  /** Move instrument track `fromIndex` → `toIndex` (undoable). */
  reorderTracks: (fromIndex: number, toIndex: number) => void;
  copyPattern: (patternId: string) => void;
  cutPattern: (patternId: string) => void;
  pastePattern: (afterPatternId?: string) => void;
  copyTrack: (patternId: string, track: number) => void;
  pasteTrack: (patternId: string, track: number) => void;
  /** Copy instrument slot (preset/patch, mix, slot FX, bank links) — not pattern notes. */
  copyInstrument: (track: number) => void;
  /** Paste previously copied instrument onto this track (one undo step). */
  pasteInstrument: (track: number) => void;
  /** Sync tracker to pattern/track. Pass `{ focus: false }` to keep arrange panel focus for shortcuts. */
  openPatternTrack: (patternId: string, track: number, options?: { focus?: boolean }) => void;
  selectScene: (sceneId: string) => void;
  createScene: () => void;
  renameScene: (sceneId: string, name: string) => void;
  deleteScene: (sceneId: string) => void;
  setScenePatternOrder: (sceneId: string, patternIds: string[]) => void;
  playScene: () => void;
  newProject: () => void;
  /** Append one instrument track (up to max). */
  addTrack: () => void;
  /** Remove one instrument track (keeps at least one). */
  removeTrack: (track: number) => void;
  /** Clone track and insert the copy immediately to its right. */
  duplicateTrack: (track: number) => void;
  importProject: () => Promise<void>;
  /** Import a Standard MIDI File as a new clip + instruments (keeps bank & FX). */
  importMidi: () => Promise<void>;
  /** Export the arrangement (all patterns in list order) as a Standard MIDI File. */
  exportMidi: () => void;
  exportProject: () => void | Promise<void>;
  bounce: (scope: PlayScope) => Promise<void>;
  bounceStems: (scope: PlayScope) => Promise<void>;
  /** Bounce the loop brace offline into a new sampler track and open Author. */
  bounceToSampler: () => Promise<void>;
  /**
   * Toggle transport mic record (overdub — does not stop playback). Start
   * auto-enables audio; stop creates a new sampler track and loads the recording.
   */
  toggleMicRecord: () => Promise<void>;
  /** Live mic RMS while transport recording (0 when idle). */
  getMicRecordLevel: () => number;
  loadDemo: (id: LabDemoId) => void;
  applyDsl: () => void;
  applyGridToDsl: () => void;
  toggleDslPanel: () => void;
  toggleStatusPanel: () => void;
  setMaterial: (mode: MaterialMode) => void;
  toggleHelp: () => void;
  toggleGoTo: () => void;
  /** Open / close the Generate Notes floating panel. */
  toggleGenerateNotes: () => void;
  /** Open / close the Mixer floating panel. */
  toggleMixer: () => void;
  /** Open / close the Performance (session) grid. */
  togglePerformance: () => void;
  /** Open / close Settings (MIDI / control routing). */
  toggleSettings: () => void;
  startPerformance: () => void;
  stopPerformance: () => void;
  /** Arm / finish live Performance bus capture → sample library. */
  togglePerformanceRecord: () => void;
  /** Launch / toggle one track cell (pattern × track). */
  launchPerformanceCell: (patternId: string, track: InstrumentSlot) => void;
  /** Launch / toggle a whole pattern row (all tracks). */
  launchPerformancePattern: (patternId: string) => void;
  /** Queue stop for one Performance column at its lane pattern-end. */
  stopPerformanceLane: (track: InstrumentSlot) => void;
  /** Commit Generate Notes into the current selection/cursor (undoable). */
  applyGenerateNotes: (rowNotes: Array<number | null>) => void;
  /** Jump tracker cursor to a row (Go-to modal). */
  goToRow: (step: number) => void;
  /** Return keyboard focus to the tracker grid (modal dismiss). */
  focusTracker: () => void;
  /** Open floating row-label editor for a tracker step. */
  openRowLabel: (
    step: number,
    marker: { label: string; color: number } | undefined,
    anchor: { left: number; top: number; bottom: number; right: number },
  ) => void;
  /** Commit or clear a row marker from the React row-label modal. */
  commitRowLabel: (step: number, marker: { label: string; color: number } | null) => void;
  /** Open floating track color picker for a tracker column. */
  openTrackLabel: (
    track: number,
    color: number | null,
    anchor: { left: number; top: number; bottom: number; right: number },
  ) => void;
  /** Apply project-global track column tint immediately (`null` clears). */
  setTrackColor: (track: number, color: number | null) => void;
  /** Push FX stack to engine + persist (projectStore already patched by FxDock). */
  applyFxStack: (effects: FxNode[]) => void;
  /** Push per-slot insert FX + persist. */
  applySlotFxStack: (slot: InstrumentSlot, effects: FxNode[]) => void;
  getFxStack: () => FxNode[];
  /** Live-only bypass of the master FX stack (default off). */
  setFxBypass: (on: boolean) => void;
  /** Switch FX dock between master bus and current instrument. */
  setFxScope: (scope: "master" | "track") => void;
  persistPanels: (panels: NonNullable<LabPersistPartial["panels"]>) => void;
  setStatus: (message: string) => void;
  openInstrument: (slot?: InstrumentSlot) => void;
  closeInstrument: () => void;
  setInstrumentSlot: (slot: InstrumentSlot) => void;
  openInstrumentAuthor: () => void;
  closeInstrumentAuthor: () => void;
  closeArrange: () => void;
  updateInstrument: (slot: InstrumentSlot, instrument: Instrument) => void;
  updateInstrumentPreset: (slot: InstrumentSlot, preset: SynthPreset) => void;
  updateInstrumentKit: (slot: InstrumentSlot, kit: DrumKit) => void;
  updateInstrumentSf2Patch: (slot: InstrumentSlot, patch: Sf2Patch) => void;
  updateInstrumentExternalMidiPatch: (slot: InstrumentSlot, patch: ExternalMidiPatch) => void;
  /** Web MIDI outputs available for External MIDI instruments. */
  listExternalMidiOutputs: () => Array<{ id: string; name: string }>;
  refreshExternalMidiAccess: () => Promise<boolean>;
  updateInstrumentWavetablePreset: (slot: InstrumentSlot, preset: WavetablePreset) => void;
  updateInstrumentSamplerPreset: (slot: InstrumentSlot, preset: SamplerPreset) => void;
  ensureSamplerSamples: (slot: InstrumentSlot) => Promise<void>;
  /** Load sample-mode drum pad PCM into the engine. */
  ensureDrumSamples: (slot: InstrumentSlot) => Promise<void>;
  /** Rename a host library sample (undoable). */
  renameSample: (id: string, name: string) => Promise<void>;
  /** Delete a host library sample (undoable until reload). */
  deleteSample: (id: string) => Promise<void>;
  /** Create a nested sample-library folder (undoable). Returns folder id. */
  createSampleFolder: (name: string, parentId?: string | null) => Promise<string | undefined>;
  /** Rename a sample-library folder (undoable). */
  renameSampleFolder: (id: string, name: string) => Promise<void>;
  /** Delete a folder and its contents (undoable until reload). */
  deleteSampleFolder: (id: string) => Promise<void>;
  /** Move a sample into a folder or Library root (undoable). */
  moveSampleToFolder: (sampleId: string, folderId: string | null) => Promise<void>;
  /** Reparent a folder (undoable). */
  moveSampleFolder: (folderId: string, parentId: string | null) => Promise<void>;
  /** Duplicate a library sample into a folder (or root). Returns new meta id. */
  duplicateSample: (
    sampleId: string,
    folderId?: string | null,
  ) => Promise<SampleAssetMeta | undefined>;
  /** Download a library sample as a mono WAV file. */
  exportSampleWav: (sampleId: string) => Promise<void>;
  /**
   * Decode and ingest audio files into the host sample library only
   * (does not assign them to a sampler slot). Returns created metas.
   * Optional `folderId` places imports into that folder.
   */
  importLibrarySamples: (
    files: File[],
    folderId?: string | null,
  ) => Promise<SampleAssetMeta[]>;
  /** Export the full host sample library as `.adamsamples`. */
  exportSampleLibrary: () => Promise<void>;
  /** Merge a `.adamsamples` package into the host library. */
  importSampleLibrary: () => Promise<void>;
  /** Permanently wipe the host sample library (not undoable). */
  wipeSampleLibrary: () => Promise<void>;
  /**
   * Render filled sampler pads to separate library samples (new folder),
   * save a sample-mode drum kit, and add a new drums track.
   */
  renderSamplerPadsToDrums: (slot: InstrumentSlot) => Promise<void>;
  importSamplerAudio: (slot: InstrumentSlot, file: File) => Promise<void>;
  /** Ingest already-decoded mono PCM (record / bounce). */
  ingestSamplerPcm: (
    slot: InstrumentSlot,
    options: {
      name: string;
      pcm: Float32Array;
      sampleRate: number;
      source?: "import" | "record" | "bounce";
    },
  ) => Promise<void>;
  /** Live AudioContext when the engine is in worklet mode (Author preview). */
  getAudioContext: () => AudioContext | undefined;
  setSlotMix: (slot: InstrumentSlot, partial: Partial<SlotMix>) => void;
  renameTrack: (slot: InstrumentSlot, name: string) => void;
  toggleMute: (slot: InstrumentSlot) => void;
  /** Toggle per-pattern mute for a track cell (arrange matrix). */
  togglePatternMute: (patternId: string, track: number) => void;
  toggleSolo: (slot: InstrumentSlot) => void;
  setInstrumentKind: (
    slot: InstrumentSlot,
    kind: "synth" | "drums" | "sf2" | "wavetable" | "sampler" | "externalMidi",
  ) => void;
  resetInstrumentSlot: (slot: InstrumentSlot) => void;
  instrumentNoteOn: (slot: InstrumentSlot, note: number, velocity: number) => void;
  instrumentNoteOff: (slot: InstrumentSlot, note: number) => void;
  applyInstrumentBankEntry: (slot: InstrumentSlot, bankId: string) => void;
  saveInstrumentBank: (slot: InstrumentSlot) => void;
  saveInstrumentBankAs: (slot: InstrumentSlot) => void;
  applyDrumBankEntry: (slot: InstrumentSlot, bankId: string) => void;
  saveDrumBank: (slot: InstrumentSlot) => void;
  saveDrumBankAs: (slot: InstrumentSlot) => void;
  applyWavetableBankEntry: (slot: InstrumentSlot, bankId: string) => void;
  saveWavetableBank: (slot: InstrumentSlot) => void;
  saveWavetableBankAs: (slot: InstrumentSlot) => void;
  renameWavetableBank: (bankId: string) => void;
  deleteWavetableBank: (bankId: string) => void;
  exportWavetableBank: (bankId: string) => void;
  exportAllWavetableBank: () => void;
  importWavetableBank: () => void;
  editWavetableBankTags: (bankId: string) => void;
  setWavetableBankCategory: (bankId: string, category: WtPresetCategoryId) => void;
  applySamplerBankEntry: (slot: InstrumentSlot, bankId: string) => void;
  saveSamplerBank: (slot: InstrumentSlot) => void;
  saveSamplerBankAs: (slot: InstrumentSlot) => void;
  renameSamplerBank: (bankId: string) => void;
  deleteSamplerBank: (bankId: string) => void;
  exportSamplerBank: (bankId: string) => void;
  exportAllSamplerBank: () => void;
  importSamplerBank: () => void;
  editSamplerBankTags: (bankId: string) => void;
  setSamplerBankCategory: (bankId: string, category: SamplerPresetCategoryId) => void;
  renameInstrumentBank: (bankId: string) => void;
  deleteInstrumentBank: (bankId: string) => void;
  exportInstrumentBank: (bankId: string) => void;
  exportAllInstrumentBank: () => void;
  importInstrumentBank: () => void;
  editInstrumentBankTags: (bankId: string) => void;
  setInstrumentBankCategory: (bankId: string, category: PresetCategoryId) => void;
};

let actions: LabActions | null = null;

export function getLabActions(): LabActions | null {
  return actions;
}

export function setLabActions(next: LabActions | null): void {
  actions = next;
}
